import {EventID, EventMap, IAsyncEventDecorator, IEventManagerWithAsync, IService, Listener, ListenerPromiseType, SystemComponentType} from '@gongxigames/framework/core/types';
import {CoreDI} from '@gongxigames/framework/core/configs';
import {PromiseEventDecorator} from '@gongxigames/framework/core/services/eventManager/PromiseEventDecorator';
import {AsyncEventDecorator} from '@gongxigames/framework/core';
import {injectable} from '@gongxigames/framework/core/installer';
import {consoleLogger} from '@gongxigames/framework/core';
import {checkIsEventDeprecated} from '@gongxigames/framework/core/services/eventManager/checkIsEventDeprecated';

/**
 * game events handler and dispatcher. represents the way of communication between modules
 * all the global events goes through this class (it should be injected into every module)
 */
@injectable()
export class EventManagerWithAsync implements IEventManagerWithAsync, IService {
    public static NAME: symbol = CoreDI.IEventManagerWithAsync;
    public type: SystemComponentType = 'service';

    protected _eventMap: EventMap = {};

    protected _asyncEventsDecorator: IAsyncEventDecorator = new AsyncEventDecorator();

    protected _promiseEventDecorator: PromiseEventDecorator = new PromiseEventDecorator();

    public destroy(): void {
        this.clear();
    }

    public clear(): void {
        this._eventMap = {};
    }

    public onEvent(id: string | string[], listener: Function, owner: Object, priority = 0): void {
        this.addListenerAs(false, id, listener, owner, priority);
    }

    public emitEvent(id: string, ...args: any[]): void {
        this.emit(id, ...args);
    }

    public offEvent(id: string, owner: Object): void {
        this.removeListenerFrom(false, id, owner);
    }

    public hasEventListener(id: string, owner: Object): boolean {
        return this.hasListenerAt(false, id, owner);
    }

    public onAsyncEvent(id: string | string[], listener: Function, owner: Object, priority = 0): void {
        this.addListenerAs(false, id, listener, owner, priority);
    }

    public emitAsyncEvent(id: string, ...args: any[]): void {
        this.emitAsync(id, ...args);
    }

    public offAsyncEvent(id: string, owner: Object): void {
        this.removeListenerFrom(true, id, owner);
    }

    // TODO: remove
    public hasAsyncEventListener(id: string, owner: Object): boolean {
        return this.hasListenerAt(true, id, owner);
    }

    public on(id: EventID | EventID[], callback: Function, owner: Object, priority = 0): void {
        this.addListenerAs(false, id, callback, owner, priority);
    }

    public off(id: EventID | EventID[], owner: Object): void {
        this.removeListenerFrom(false, id, owner);
    }

    public offAllFromOwner(owner: Object): void {
        this.removeAllListenersFromOwner(false, owner);
    }

    public hasListener(id: EventID, owner: Object): boolean {
        return this.hasListenerAt(false, id, owner);
    }

    public emit(id: EventID, ...args: any[]): void {
        checkIsEventDeprecated(id);

        const listeners: Listener[] = this._eventMap[id as any];
        const eventName = (typeof id === 'symbol') ? id.description : id;

        consoleLogger.log('⚡︎ EVENT:', eventName);

        listeners
            ?.slice()
            .forEach(listener => listener.callback(...args));
    }

    public emitPromise(id: EventID, ...args: any[]): Promise<void[]> {
        return this._promiseEventDecorator.emitEvent(id, ...args);
    }

    public emitAsync(id: EventID, ...args: any[]): void {
        this._asyncEventsDecorator.addEvent(id, ...args);
    }

    public onAsync(id: EventID | EventID[], callback: Function, owner: Object, priority = 0): void {
        this.addListenerAs(true, id, callback, owner, priority);
    }

    public offAsync(id: EventID | EventID[], owner: Object): void {
        this.removeListenerFrom(true, id, owner);
    }

    // TODO: remove
    public hasAsyncListener(id: EventID, owner: Object): boolean {
        return this.hasListenerAt(true, id, owner);
    }

    public dispatchAsyncEvents(): void {
        this._asyncEventsDecorator.dispatchAsyncEvents();
    }

    public onPromise(id: EventID | EventID[], callback: ListenerPromiseType<any>, owner: Object): void {
        this._promiseEventDecorator.onPromise(id, callback, owner);
    }

    public offPromise(id: EventID | EventID[], owner: Object): void {
        this._promiseEventDecorator.offPromise( id, owner);
    }

    public offAllPromises(owner: Object): void {
        this._promiseEventDecorator.offAllPromises(owner);
    }

    public getNumberEvents(): number {
        let count = 0;

        const getCountEvents = (eventsMap: {[id: string]: [];}): number => {
            let count = 0;

            for (const id in eventsMap) {
                if (eventsMap.hasOwnProperty(id)) {
                    count += eventsMap[id].length;
                }
            }

            return count;
        };

        count += getCountEvents(this._eventMap as any);
        count += getCountEvents(this._asyncEventsDecorator.asyncEventMap as any);

        return count;
    }

    private addListenerAs(isAsync: boolean, id: EventID | EventID[], callback: Function, owner: Object, priority: number): void {
        const currentEventsMap = this.getAppropriateEventMap(isAsync);
        const ids = id instanceof Array ? id : [id];

        ids.forEach(id => {
            checkIsEventDeprecated(id);

            if (!currentEventsMap[id as any]) {
                currentEventsMap[id as any] = [];
            }

            currentEventsMap[id as any].push({callback, owner, priority});
            currentEventsMap[id as any].sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
        });
    }

    private removeListenerFrom(isAsync: boolean, id: EventID | EventID[], owner: Object): void {
        const currentEventsMap = this.getAppropriateEventMap(isAsync);
        const ids = id instanceof Array ? id : [id];

        ids.forEach(id => {
            const listeners: Listener[] = currentEventsMap[id as any] || [];

            currentEventsMap[id as any] = listeners.filter( (listener: Listener) => listener.owner !== owner );
        });
    }

    private removeAllListenersFromOwner(isAsync: boolean, owner: Object): void {
        const currentEventsMap = this.getAppropriateEventMap(isAsync);
        const objectSymbols = Object.getOwnPropertySymbols(currentEventsMap);

        for (const key in currentEventsMap) {
            if (currentEventsMap.hasOwnProperty(key)) {
                currentEventsMap[key] = currentEventsMap[key].filter( (listener: Listener) => listener.owner !== owner );
            }
        }

        objectSymbols.forEach(symbol => {
            currentEventsMap[symbol] = currentEventsMap[symbol].filter( (listener: Listener) => listener.owner !== owner );
        });
    }

    private hasListenerAt(isAsync: boolean, id: EventID, owner: Object): boolean {
        const currentEventsMap = this.getAppropriateEventMap(isAsync);
        const listeners: Listener[] = currentEventsMap[id as any] || [];

        return listeners.some(listener => listener.owner === owner);
    }

    private getAppropriateEventMap(isAsync: boolean): EventMap {
        return isAsync ? this._asyncEventsDecorator.asyncEventMap : this._eventMap;
    }
}
